import { Component, OnInit, OnDestroy } from '@angular/core';
import { EnumsService } from '@library/core/services';
import { ShipmentService } from '../shipment.service';
import { merge, Observable, of, Subject } from 'rxjs';
import { FormArray } from '@angular/forms';
import { accessorialsGroup } from '@library/common/models';
import { map, switchMap, takeUntil } from 'rxjs/operators';

interface AccessorialWithIndex {
   description: string;
   code?: string;
   isPrimary?: boolean;
   module?: string;
   formIndex: number;
}

@Component({
   selector: 'shipment-accessorials',
   templateUrl: './accessorials.component.html',
   styleUrls: ['./accessorials.component.scss']
})
export class AccessorialsComponent implements OnInit, OnDestroy {
   accessorials: any[] = []; // all accessorials filtered with booktype
   filteredAccessorials$: Observable<AccessorialWithIndex[]>; // accessorials that is shown ( searching / primary / less / more )
   selectedAccessorials = new FormArray([]); // all selected accessorials
   showAll: boolean = false;
   primaryColor = 'primary';
   
   private destroy$ = new Subject<void>();
   searching: boolean = false;
   searchTerm: string;

   constructor(
      public enums: EnumsService,
      public shipment: ShipmentService,
   ) { }

   ngOnInit() {
      this.selectedAccessorials = this.shipment.shipmentForm.get('accessorials') as FormArray;
      
      merge(
         // run when init
         this.enums.accessorials(),
         
         // run whenever booktype is selected
         this.shipment.shipmentType$.pipe(
           switchMap(() => this.enums.accessorials())
         )
       )
       .pipe(
            map(list => list.sort(( a, b ) => {
               const descA = a.description.toLowerCase();
               const descB = b.description.toLowerCase();
               return descA < descB ? -1 : descA > descB ? 1 : 0;
            })),
            takeUntil(this.destroy$)
         )
       .subscribe(accessorials => {
         this.filterBooktype(accessorials);
         this.filterPrimary();
       });
   }

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }

   private createFilteredWithIndex(filteredAccessorials: any[]): AccessorialWithIndex[] {
      return filteredAccessorials.map(accessorial => {
         const originalIndex = this.accessorials.findIndex(a => 
            a.code === accessorial.code && a.description === accessorial.description
         );
         
         return {
            ...accessorial,
            formIndex: originalIndex >= 0 ? originalIndex : 0
         };
      });
   }

   onSearch(event: Event): void {
      const input = event.target as HTMLInputElement;
      this.searchTerm = input.value.toLowerCase().trim();
      this.searching = this.searchTerm ? true : false;
      
      if (!this.searchTerm) {
         this.showAll ? this.filterSeeMore() : this.filterPrimary();
         return;
      }
      
      const filtered = this.accessorials.filter(service =>{
         const desc = service.description?.toLowerCase().split(/\s+/);
         // search conatins space (check first word of search if startswith any description) - search full string
         if(this.searchTerm.includes(' ') && desc.some(word => word.startsWith(this.searchTerm.toLowerCase().split(/\s+/)[0]))) {
            return service.description?.toLowerCase().includes(this.searchTerm);
         } else { // search single word - search starts with
            return desc.some(word => word.startsWith(this.searchTerm)) 
            || service.description.toLowerCase().startsWith(this.searchTerm);
         }
      });
      
      this.filteredAccessorials$ = of(this.createFilteredWithIndex(filtered));
   }

   onSelectionChange(accessorial: any, isChecked: boolean): void {
      
      if (isChecked) {
         // Add to FormArray when selected
         const accessorialFormGroup = accessorialsGroup({
            ...accessorial,
            selected: true
         });
         this.selectedAccessorials.push(accessorialFormGroup);
      } else {
         // Remove from FormArray when unselected
         const indexToRemove = this.selectedAccessorials.controls.findIndex(control => 
            control.get('code')?.value === accessorial.code && 
            control.get('description')?.value === accessorial.description
         );
         
         if (indexToRemove >= 0) {
            this.selectedAccessorials.removeAt(indexToRemove);
         }
      }
      
   }

   filterPrimary() {
      const primaryAccessorials = this.accessorials.filter(service => service.isPrimary === true);
      this.filteredAccessorials$ = of(this.createFilteredWithIndex(primaryAccessorials));
   }

   filterSeeMore() {
      this.filteredAccessorials$ = of(this.createFilteredWithIndex(this.accessorials));
   }

   filterBooktype(accessorials: any[]) {
      this.accessorials = accessorials.filter(service => 
         service.module.includes( this.shipment.shipType ) || service.isPrimary == true
      );


      // filter selectedAccessorials using both code and description - this is for domestic/international trigger
      const filteredControls = this.selectedAccessorials.controls.filter(control => {
         const code = control.get('code')?.value;
         const description = control.get('description')?.value;
      
         return this.accessorials.some(service =>
         service.code === code && service.description === description
         );
      });
      
      this.selectedAccessorials.clear();
      filteredControls.forEach(ctrl => this.selectedAccessorials.push(ctrl));

   }

   isSelected(accessorial: any): boolean {
      return this.selectedAccessorials.controls.some(control => 
         control.get('code')?.value === accessorial.code && 
         control.get('description')?.value === accessorial.description
      );
   }

   trackByCode(index: number, item: any): string {
      return item.code || item.description || index.toString();
   }


   getSelectedAccessorials(): any[] {
      if (!this.selectedAccessorials || this.selectedAccessorials.length === 0) {
         return [];
      }
      
      // Since FormArray only contains selected items, return all controls
      return this.selectedAccessorials.controls.map(control => control.value);
   }

   removeSelection(selectedItem: any): void {
      // Find and remove the item from FormArray
      const indexToRemove = this.selectedAccessorials.controls.findIndex(control => 
         control.get('code')?.value === selectedItem.code && 
         control.get('description')?.value === selectedItem.description
      );
      
      if (indexToRemove >= 0) {
         this.selectedAccessorials.removeAt(indexToRemove);
      }
   }

   toggleSeeMore(): void {
      this.showAll = !this.showAll;
      this.showAll ? this.filterSeeMore() : this.filterPrimary();
   }

   clearSearch(): void {
      this.searching = false;
      this.searchTerm = "";
      this.showAll ? this.filterSeeMore() : this.filterPrimary();

   }
}